import React, { PureComponent } from 'react';
import track, { TrackingPropType } from 'react-tracking';
import { bool, shapeOf, string } from 'prop-types';
import { withAnalytics, safeBuildEventObj } from '@ynap/analytics';
import BEMHelper from '@ynap/bem-helper';
import { withMessages } from '@ynap/immutables-provider';
import { trackVideoCompleted, trackVideoPlay } from './analytics';

const bem = new BEMHelper('HtmlVideoPlayer');
@track()
class HtmlVideoPlayer extends PureComponent {
    constructor(props) {
        super(props);

        this.seekbarProgress = React.createRef();
        this.seekbarBuffered = React.createRef();

        this.state = {
            paused: true,
            trackPlayed: false,
            trackCompleted: false,
        };

        this.handleClick = this.handleClick.bind(this);
        this.updateProgressBar = this.updateProgressBar.bind(this);
        this.onPlay = this.onPlay.bind(this);
        this.safeTrackEvent = this.safeTrackEvent.bind(this);
    }

    safeTrackEvent(buildAnalyticsFunc, ...args) {
        const { analytics, tracking } = this.props;
        tracking.trackEvent(safeBuildEventObj(buildAnalyticsFunc, analytics, ...args));
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.intersected && this.props.intersected) {
            const playPromise = this.video.play();

            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        // Autoplay started!
                    })
                    .catch(() => {
                        // Auto-play was prevented
                    });
            }
        } else if (prevProps.intersected && !this.props.intersected) {
            this.video.pause();
        }

        this.setState({
            paused: this.video.paused,
        });
    }

    onPlay() {
        const { video } = this.props;
        const { trackPlayed } = this.state;

        if (!trackPlayed) {
            this.safeTrackEvent(trackVideoPlay, video);
            this.setState({ trackPlayed: true });
        }
    }

    handleClick(e) {
        const video = e.target;
        video.paused ? video.play() : video.pause();
        this.setState({
            paused: video.paused,
        });
    }

    updateProgressBar(e) {
        const videoComponent = e.target;
        const { video } = this.props;
        const { trackCompleted } = this.state;
        const value = ((videoComponent.currentTime / videoComponent.duration) * 100).toFixed(2);
        this.seekbarProgress.current.style.width = `${value  }%`;

        const percentageBuffered = ((videoComponent.buffered.end(0) - videoComponent.buffered.start(0)) / videoComponent.duration) * 100;
        this.seekbarBuffered.current.style.width = `${percentageBuffered  }%`;

        if (value > 95 && !trackCompleted) {
            this.safeTrackEvent(trackVideoCompleted, video);
            this.setState({
                trackCompleted: true,
            });
        }
    }

    render() {
        const { className, messages, video } = this.props;
        const { paused } = this.state;
        const playPause = messages?.video?.playPause();

        return (
            <div className={bem('wrapper')}>
                <button aria-label={playPause} className={bem('playButtonIcon', paused ? 'iconPlay' : 'iconPause', 'playButtonIcon')} />

                <video
                    loop
                    muted
                    playsInline
                    className={bem('video', null, className)}
                    onClick={this.handleClick}
                    onPlay={this.onPlay}
                    onTimeUpdate={this.updateProgressBar}
                    poster={video.thumbnail?.url}
                    preload="none"
                    ref={(node) => { this.video = node; }}
                >
                    <source src={video.url} type="video/mp4" />
                </video>

                <div className={bem('video-controls')}>
                    <input className={bem('seekbar', 'seekbarFull')} type="range" />
                    <div className={bem('seekbar', 'seekbarProgress')} ref={this.seekbarProgress} />
                    <div className={bem('seekbar', 'seekbarBuffered')} ref={this.seekbarBuffered} />
                </div>
            </div>
        );
    }
}

HtmlVideoPlayer.propTypes = {
    video: shapeOf({
        thumbnail: shapeOf({
            url: string,
        }),
        url: string.isRequired,
    }),
    intersected: bool,
    analytics: shapeOf({
        category: shapeOf({
            pageType: string.isRequired,
            subCategory1: string.isRequired,
        }),
    }),
    tracking: TrackingPropType,
};
export { HtmlVideoPlayer };
export default withMessages()(withAnalytics(HtmlVideoPlayer));
